import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
    Container,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Checkbox,
    Chip,
    Divider,
    Tooltip,
    Snackbar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { supabase } from '../services/supabaseClient';
import { MODULE_CATEGORY_MAP } from '../constants/modules';

interface Perfil {
    id: string;
    nome: string;
}

interface Modulo {
    id: string;
    nome: string;
}

const SYSTEM_PROTECTED_PROFILES = ['Administrador', 'Usuário Padrão'];

const PerfilManagementPage: React.FC = () => {
    const [perfis, setPerfis] = useState<Perfil[]>([]);
    const [allModulos, setAllModulos] = useState<Modulo[]>([]);
    // Mapa perfil_id -> array de nomes de módulos habilitados
    const [perfilModulosMap, setPerfilModulosMap] = useState<Record<string, Modulo[]>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [newPerfilName, setNewPerfilName] = useState('');
    const [createLoading, setCreateLoading] = useState(false);

    const [editPerfil, setEditPerfil] = useState<Perfil | null>(null);
    const [editPerfilName, setEditPerfilName] = useState('');
    const [selectedModulos, setSelectedModulos] = useState<string[]>([]);
    const [updateLoading, setUpdateLoading] = useState(false);

    const [deletePerfil, setDeletePerfil] = useState<Perfil | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });

    const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const loadAllData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [{ data: perfisData, error: perfisError }, { data: modulosData, error: modulosError }, { data: acessosData }] =
                await Promise.all([
                    supabase.rpc('get_perfis'),
                    supabase.rpc('get_modulos'),
                    supabase.from('perfil_modulo_acesso').select('perfil_id, modulo_id'),
                ]);

            if (perfisError) throw perfisError;
            if (modulosError) throw modulosError;

            const loadedPerfis: Perfil[] = perfisData || [];
            const loadedModulos: Modulo[] = modulosData || [];
            setPerfis(loadedPerfis);
            setAllModulos(loadedModulos);

            const modById: Record<string, Modulo> = {};
            loadedModulos.forEach((m) => {
                modById[m.id] = m;
            });

            const map: Record<string, Modulo[]> = {};
            loadedPerfis.forEach((p) => {
                map[p.id] = [];
            });
            (acessosData || []).forEach((row: any) => {
                const mod = modById[row.modulo_id];
                if (mod) {
                    if (!map[row.perfil_id]) map[row.perfil_id] = [];
                    map[row.perfil_id].push(mod);
                }
            });
            Object.keys(map).forEach((pid) => {
                map[pid].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
            });
            setPerfilModulosMap(map);
        } catch (err: any) {
            setError(err.message || 'Erro ao carregar dados de perfis e módulos.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAllData();
    }, [loadAllData]);

    const handleCreatePerfil = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newPerfilName.trim()) return;
        setCreateLoading(true);
        setError(null);
        try {
            const { data: created, error } = await supabase.rpc('create_perfil', { p_nome: newPerfilName.trim() });
            if (error) throw error;
            setNewPerfilName('');
            showSnackbar(`Perfil "${created?.nome || newPerfilName.trim()}" criado! Selecione os módulos de acesso.`, 'success');
            await loadAllData();
            if (created && created.id) {
                setEditPerfil(created);
                setEditPerfilName(created.nome);
                setSelectedModulos([]);
            }
        } catch (err: any) {
            setError(err.message || 'Erro ao criar perfil.');
        } finally {
            setCreateLoading(false);
        }
    };

    const handleToggleModulo = (moduloId: string) => {
        setSelectedModulos((prev) =>
            prev.includes(moduloId) ? prev.filter((id) => id !== moduloId) : [...prev, moduloId]
        );
    };

    const handleOpenEditDialog = async (perfil: Perfil) => {
        setEditPerfil(perfil);
        setEditPerfilName(perfil.nome);
        setError(null);
        const currentMods = perfilModulosMap[perfil.id];
        if (currentMods) {
            setSelectedModulos(currentMods.map((m) => m.id));
        } else {
            try {
                const { data, error } = await supabase.rpc('get_modulos_by_perfil', { p_perfil_id: perfil.id });
                if (error) throw error;
                setSelectedModulos((data || []).map((m: Modulo) => m.id));
            } catch (err: any) {
                setError(err.message || 'Erro ao carregar módulos do perfil.');
            }
        }
    };

    const handleUpdatePerfil = async () => {
        if (!editPerfil || !editPerfilName.trim()) return;
        setUpdateLoading(true);
        setError(null);
        try {
            const { error: updateNameError } = await supabase.rpc('update_perfil', {
                p_id: editPerfil.id,
                p_nome: editPerfilName.trim(),
            });
            if (updateNameError) throw updateNameError;

            const { error: updateModulosError } = await supabase.rpc('update_perfil_modulos', {
                p_perfil_id: editPerfil.id,
                p_modulo_ids: selectedModulos,
            });
            if (updateModulosError) throw updateModulosError;

            setEditPerfil(null);
            setEditPerfilName('');
            setSelectedModulos([]);
            showSnackbar('Perfil e permissões atualizados com sucesso!', 'success');
            loadAllData();
        } catch (err: any) {
            setError(err.message || 'Erro ao atualizar perfil.');
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleDeletePerfil = async () => {
        if (!deletePerfil) return;
        setDeleteLoading(true);
        setError(null);
        try {
            const { error } = await supabase.rpc('delete_perfil', { p_id: deletePerfil.id });
            if (error) throw error;
            showSnackbar(`Perfil "${deletePerfil.nome}" excluído com sucesso.`, 'success');
            setDeletePerfil(null);
            loadAllData();
        } catch (err: any) {
            setError(err.message || 'Erro ao excluir perfil.');
            setDeletePerfil(null);
        } finally {
            setDeleteLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const isEditingAdmin = editPerfil?.nome === 'Administrador';

    return (
        <Container component="main" maxWidth="md">
            <Box sx={{ my: 4 }}>
                <Box sx={{ mb: 3 }}>
                    <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
                        Gerenciamento de Perfis
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Configure os perfis de usuário e defina a quais módulos cada perfil possui acesso
                    </Typography>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Criar Perfil */}
                <Paper
                    elevation={0}
                    sx={{ p: 2.5, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                        Criar Novo Perfil
                    </Typography>
                    <Box component="form" onSubmit={handleCreatePerfil} sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            size="small"
                            label="Nome do Novo Perfil (ex: Engenheiro, Financeiro)"
                            variant="outlined"
                            fullWidth
                            value={newPerfilName}
                            onChange={(e) => setNewPerfilName(e.target.value)}
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={createLoading ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                            disabled={createLoading || !newPerfilName.trim()}
                            sx={{ px: 3, fontWeight: 600, flexShrink: 0 }}
                        >
                            Criar Perfil
                        </Button>
                    </Box>
                </Paper>

                {/* Listar Perfis */}
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Perfil</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Módulos Habilitados</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                    Ações
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {perfis.map((perfil) => {
                                const mods = perfilModulosMap[perfil.id] || [];
                                const isProtected = SYSTEM_PROTECTED_PROFILES.includes(perfil.nome);

                                return (
                                    <TableRow key={perfil.id} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {perfil.nome}
                                                {isProtected && (
                                                    <Chip label="Padrão" size="small" variant="outlined" color="info" />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Chip
                                                    label={`${mods.length} de ${allModulos.length} módulos`}
                                                    size="small"
                                                    color={
                                                        mods.length === 0
                                                            ? 'warning'
                                                            : mods.length === allModulos.length
                                                            ? 'success'
                                                            : 'primary'
                                                    }
                                                    variant={mods.length === 0 ? 'filled' : 'outlined'}
                                                    sx={{ fontWeight: 600 }}
                                                />
                                                {mods.length > 0 && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        ({mods.slice(0, 4).map((m) => m.nome).join(', ')}
                                                        {mods.length > 4 ? ` +${mods.length - 4}` : ''})
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Editar nome e módulos de acesso">
                                                <IconButton color="primary" onClick={() => handleOpenEditDialog(perfil)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip
                                                title={
                                                    isProtected
                                                        ? 'Perfis padrões do sistema não podem ser excluídos'
                                                        : 'Excluir perfil'
                                                }
                                            >
                                                <span>
                                                    <IconButton
                                                        color="error"
                                                        disabled={isProtected}
                                                        onClick={() => setDeletePerfil(perfil)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Diálogo de Edição de Perfil e Permissões */}
            <Dialog
                open={Boolean(editPerfil)}
                onClose={updateLoading ? undefined : () => setEditPerfil(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Editar Perfil e Permissões</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <TextField
                            margin="dense"
                            label="Nome do Perfil"
                            type="text"
                            fullWidth
                            value={editPerfilName}
                            disabled={isEditingAdmin}
                            helperText={
                                isEditingAdmin ? 'O nome do perfil Administrador não pode ser alterado.' : undefined
                            }
                            onChange={(e) => setEditPerfilName(e.target.value)}
                        />

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Módulos Liberados ({selectedModulos.length}/{allModulos.length})
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    size="small"
                                    onClick={() => setSelectedModulos(allModulos.map((m) => m.id))}
                                >
                                    Selecionar Todos
                                </Button>
                                <Button
                                    size="small"
                                    color="inherit"
                                    onClick={() => setSelectedModulos([])}
                                    disabled={isEditingAdmin}
                                >
                                    Limpar Seleção
                                </Button>
                            </Box>
                        </Box>

                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.5,
                                maxHeight: 340,
                                overflowY: 'auto',
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                gap: 0.5,
                            }}
                        >
                            {allModulos.map((modulo) => {
                                const group = MODULE_CATEGORY_MAP[modulo.nome];
                                return (
                                    <FormControlLabel
                                        key={modulo.id}
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={selectedModulos.includes(modulo.id)}
                                                onChange={() => handleToggleModulo(modulo.id)}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                                                    {modulo.nome}
                                                </Typography>
                                                {group && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {group}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        sx={{ alignItems: 'flex-start', py: 0.5 }}
                                    />
                                );
                            })}
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditPerfil(null)} disabled={updateLoading}>
                        Cancelar
                    </Button>
                    <Button onClick={handleUpdatePerfil} variant="contained" disabled={updateLoading}>
                        {updateLoading ? <CircularProgress size={20} color="inherit" /> : 'Salvar Permissões'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo de Confirmação de Exclusão */}
            <Dialog open={Boolean(deletePerfil)} onClose={deleteLoading ? undefined : () => setDeletePerfil(null)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Tem certeza que deseja excluir o perfil <strong>"{deletePerfil?.nome}"</strong>? Esta ação é irreversível.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeletePerfil(null)} disabled={deleteLoading}>
                        Cancelar
                    </Button>
                    <Button onClick={handleDeletePerfil} color="error" variant="contained" disabled={deleteLoading}>
                        {deleteLoading ? <CircularProgress size={20} color="inherit" /> : 'Excluir'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4500}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default PerfilManagementPage;
